import { useMemo } from 'react';
import { useLocation } from '@remix-run/react';

import type { FilterSortOption, SearchIndex } from '../type/api';
import { usePermissions } from '@modules/user';
import { PermissionEnum } from 'generated/graphql';
import { getString, parseURL } from '@archipro-website/top-navigation';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { PagesEnum } from '~/utils/route-config';

const SORT_OPTIONS: {
    item: string;
    param: FilterSortOption;
    adminOnly?: boolean;
    searchPageOnly?: boolean;
    nonSearchPage?: boolean;
    supportCategories?: (SearchIndex | 'all')[];
    hideOnMobile?: boolean;
}[] = [
    {
        item: 'Best match',
        param: null,
        searchPageOnly: true,
    },
    {
        item: 'Featured',
        param: 'featured',
        nonSearchPage: true,
        supportCategories: ['product', 'project', 'professional'],
    },
    // IMPORTANT: "New" must come before "Popular (All time)" because it becomes
    // the default sort for articles on professional profiles (e.g. /professional/{urlSegment}/journal)
    // since "Featured" is excluded for blogpost. Moving this will change the default sort order.
    {
        item: 'New',
        param: 'new',
    },
    {
        item: 'Popular (All time)',
        param: 'popular',
    },
    {
        item: 'Trending',
        param: 'trending',
    },
    {
        item: 'Price (Low first)',
        param: 'lowPrice',
        supportCategories: ['all', 'product'],
    },
    {
        item: 'Price (High first)',
        param: 'highPrice',
        supportCategories: ['all', 'product'],
    },
    {
        item: 'Recently updated',
        param: 'updated',
        supportCategories: [
            'all',
            'product',
            'project',
            'professional',
            'blogpost',
        ],
    },
    {
        item: 'Uncategorised',
        param: 'uncategorised',
        adminOnly: true,
        supportCategories: ['all', 'product', 'project', 'professional'],
        hideOnMobile: true,
    },
    {
        item: 'Newly created',
        param: 'newlyCreated',
        adminOnly: true,
        supportCategories: ['all', 'product', 'project', 'professional'],
        hideOnMobile: true,
    },
];

interface Options {
    category?: SearchIndex;
}

export interface SortOption {
    name: string;
    value: FilterSortOption;
    selected: boolean;
    header: string;
}

const useSearchSorts = ({
    category,
}: Options): {
    sortOptions: SortOption[];
    defaultSortName: string | undefined;
} => {
    const hasMarketingView = usePermissions(PermissionEnum.BmMarketingView);
    const location = useLocation();
    const isSearchRoute = location.pathname.startsWith('/search');

    const { params } = parseURL(location.pathname + location.search);
    const selectedSort = params ? getString(params, 'sort') : undefined;
    const { mobile: isMobile } = useAppDisplayModeContext();

    const { sortOptions, defaultSortName } = useMemo(() => {
        const targetCategory = category ?? 'all';
        const isRoomsPage = location.pathname.startsWith(PagesEnum.ROOMS);
        const availableOptions = SORT_OPTIONS.filter(
            (o) =>
                (!o.supportCategories ||
                    o.supportCategories.includes(targetCategory)) &&
                (!o.searchPageOnly || (o.searchPageOnly && isSearchRoute)) &&
                (!o.nonSearchPage || (o.nonSearchPage && !isSearchRoute)) &&
                (!o.adminOnly || (o.adminOnly && hasMarketingView)) &&
                (!isMobile || !o.hideOnMobile) &&
                // Exclude Popular and Uncategorised on rooms page
                (!isRoomsPage ||
                    (o.param !== 'popular' && o.param !== 'uncategorised'))
        );

        let selectedOptionIndex = availableOptions.findIndex(
            (o) => o.param === selectedSort
        );
        if (selectedOptionIndex === -1) selectedOptionIndex = 0;

        const defaultSortName = availableOptions[0]?.item;

        const sortOptions = availableOptions.map((o, index) => {
            return {
                name: o.item,
                value: o.param,
                selected: selectedOptionIndex === index,
                header: o.item,
                state: { scroll: false },
            };
        });

        return {
            sortOptions,
            defaultSortName,
        };
    }, [
        selectedSort,
        category,
        isSearchRoute,
        hasMarketingView,
        isMobile,
        location.pathname,
    ]);

    return {
        sortOptions,
        defaultSortName,
    };
};

export { useSearchSorts };
